import { Fragment, useState } from 'react'
import { Combobox, Dialog, Transition } from '@headlessui/react'
import { classNames } from '../../../helpers/classNames'
import ApiRequest from '../../../api/ApiRequest'
import { API_AUTH_TYPES, API_ROUTES } from '../../../api/apiConstants'
import { useQuery } from 'react-query';
import { useParams } from "react-router-dom";
import { AnnotationIcon, BellIcon, CheckCircleIcon, ClipboardListIcon, ClockIcon, FolderAddIcon, FolderIcon, MailIcon, PencilAltIcon, PresentationChartBarIcon, SearchCircleIcon, SearchIcon, StarIcon, XCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/outline'
import { useEffect } from 'react'
import { FaTasks } from 'react-icons/fa'
import HHSearchNote from './Note'
import HHSearchTask from './Task'
import OngoingAdvice from './OngoingAdvice'
import { MdOutlinePublishedWithChanges } from 'react-icons/md'
import Meeting from './Meeting'
import Reminder from './Reminder'
import Activity from './Activity'
import Vault from './Vault'
import SmallLoadingSpinner from '../../../components/loading/SmallLoadingSpinner'
import useDebounce from '../../../hooks/useDebounce'
import Email from './Email'
import useKeyboardShortcut from '../../../hooks/useKeyShortcut'
import { ToolTipBottom, ToolTipHoverContainer } from '../../../components/ToolTips'



const searchApi = new ApiRequest(API_ROUTES.HOUSEHOLD_SEARCH, API_AUTH_TYPES.ADVISOR);

const icons = {
    "Task": FaTasks,
    'Ongoing Advice': MdOutlinePublishedWithChanges,
    "Note": ClipboardListIcon,
    "Activity": StarIcon,
    "Reminder": BellIcon,
    "Meeting": PresentationChartBarIcon,
    "Email": MailIcon,
    "Vault": FolderIcon
}

export default function HHSearch({ open: externalOpen, setOpen: externalSetOpen, household_id: externalHouseholdId }) {
    const { household_id: paramHouseholdId } = useParams()
    const [query, setQuery] = useState('')
    const debouncedQuery = useDebounce(query, 300);
    const [expandedSections, setExpandedSections] = useState({});
    const [expandAll, setExpandAll] = useState(false);

    // Use external household_id if provided, otherwise use params
    const household_id = externalHouseholdId || paramHouseholdId;

    const { data, isLoading } = useQuery({ queryFn: searchApi.getFn({ endpoint: `get_search_results`, params: [household_id, debouncedQuery] }), queryKey: [`hh-search`, household_id, debouncedQuery], });
    const results = data?.results || [];
    const numberOfResults = data?.number_of_results

    // Group results by data_type
    const groupedResults = results.reduce((acc, item) => {
        const type = item.data_type;
        if (!acc[type]) {
            acc[type] = [];
        }
        acc[type].push(item);
        return acc;
    }, {});

    // Get type counts for display
    const typeCounts = Object.keys(groupedResults).map(type => ({
        type,
        count: groupedResults[type].length
    }));

    // Toggle section expansion
    const toggleSection = (type) => {
        setExpandedSections(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    // Handle expand all
    const handleExpandAll = () => {
        const newExpandAll = !expandAll;
        setExpandAll(newExpandAll);
        const newExpandedSections = {};
        Object.keys(groupedResults).forEach(type => {
            newExpandedSections[type] = newExpandAll;
        });
        setExpandedSections(newExpandedSections);
    };

    // Use external state if provided, otherwise use internal state
    const [internalOpen, setInternalOpen] = useState(false);
    const open = externalOpen !== undefined ? externalOpen : internalOpen;
    const setOpen = externalSetOpen || setInternalOpen;

    const usersApi = new ApiRequest(
        API_ROUTES.USERS,
        API_AUTH_TYPES.ADVISOR
    )
    const meetingsApi = new ApiRequest(
        API_ROUTES.CLIENT_MEETINGS,
        API_AUTH_TYPES.ADVISOR
    );

    const { data: advisors } = useQuery({
        queryKey: 'advisors',
        queryFn: usersApi.getFn({ endpoint: 'index_advisors' }),
    })

    const { data: meetings } = useQuery({
        queryKey: ['meetings', paramHouseholdId],
        queryFn: meetingsApi.getFn({ endpoint: `index_household`, params: [paramHouseholdId] }),
    });
    useKeyboardShortcut(() => setOpen(true), "KeyF", true, true, false);

    return (
        <>
            <div className='my-auto'>
                <ToolTipHoverContainer>

                    <button className='btn-md btn-white  flex items-center  space-x-1' onClick={() => setOpen(true)}>
                        <SearchCircleIcon className='h-[18px] w-[18px] mt-0.5 ' />
                        <p>Search Household </p>
                    </button>
                    <ToolTipBottom>CTRL + Shift + F</ToolTipBottom>

                </ToolTipHoverContainer>
            </div>
            <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery('')} appear>
                <Dialog as="div" className="relative z-50" onClose={setOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="mx-auto max-w-5xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                                <Combobox >
                                    <div className="relative">
                                        <SearchIcon
                                            className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <Combobox.Input
                                            className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                                            placeholder="Search activities, emails, meetings, notes, tasks, ongoing advice, reminders, and vault docs"
                                            onChange={(event) => setQuery(event.target.value)}
                                        />
                                        {isLoading &&
                                            <div className="pointer-events-none absolute right-4 top-3.5 h-5 w-5 text-gray-400"
                                            >

                                                <SmallLoadingSpinner
                                                    aria-hidden="true"
                                                />
                                            </div>}
                                    </div>

                                    {(!isLoading && (query === '' || numberOfResults > 0)) && (
                                        <div className="max-h-[600px] scroll-py-2 overflow-y-auto">
                                            {/* Results Summary and Expand All */}
                                            {numberOfResults > 0 && (
                                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                                                    <div className="flex items-center space-x-4">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {numberOfResults} items found
                                                        </p>
                                                        <div className="text-sm text-gray-500">
                                                            {typeCounts.map((item, index) => (
                                                                <span key={item.type}>
                                                                    {item.count} {item.type.toLowerCase()}{item.count !== 1 ? 's' : ''}
                                                                    {index < typeCounts.length - 1 ? ', ' : ''}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={handleExpandAll}
                                                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        {expandAll ? 'Collapse All' : 'Expand All'}
                                                    </button>
                                                </div>
                                            )}

                                            {/* Grouped Results */}
                                            <Combobox.Options static className="divide-y divide-gray-100">
                                                {Object.keys(groupedResults).map((type) => {
                                                    const items = groupedResults[type];
                                                    const isExpanded = expandedSections[type] || false;
                                                    const TypeIcon = icons[type] || SearchIcon;

                                                    return (
                                                        <div key={type} className="border-b border-gray-100 last:border-b-0">
                                                            {/* Section Header */}
                                                            <button
                                                                onClick={() => toggleSection(type)}
                                                                className="w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                                                            >
                                                                {isExpanded ? (
                                                                    <ChevronDownIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                                ) : (
                                                                    <ChevronRightIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                                )}
                                                                <TypeIcon className="h-4 w-4 text-gray-500 mr-2" />
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    {type}
                                                                </span>
                                                                <span className="ml-2 text-sm text-gray-500">
                                                                    ({items.length})
                                                                </span>
                                                            </button>

                                                            {/* Section Content */}
                                                            {isExpanded && (
                                                                <div className="bg-white">
                                                                    {items.map((item) => (
                                                                        <Combobox.Option
                                                                            key={item.id}
                                                                            value={item}
                                                                            className={({ active }) =>
                                                                                classNames(
                                                                                    'flex cursor-default select-none items-center px-6 py-3 border-t border-gray-50',
                                                                                    active && 'bg-blue-50'
                                                                                )
                                                                            }
                                                                        >
                                                                            {({ active }) => (
                                                                                <div className='w-full'>
                                                                                    <div className="flex mb-2">
                                                                                        <p className="text-xs text-gray-500 ml-auto">
                                                                                            {new Date(item.created_at || item.date).toLocaleDateString()}
                                                                                        </p>
                                                                                    </div>

                                                                                    {/* Render based on the item type */}
                                                                                    {item.data_type === 'Note' && <HHSearchNote query={debouncedQuery} note={item} advisors={advisors} meetings={meetings} queryKey={`hh-search`} />}
                                                                                    {item.data_type === 'Task' && <HHSearchTask query={debouncedQuery} taskData={item} />}
                                                                                    {item.data_type === 'Ongoing Strategy' && <OngoingAdvice query={debouncedQuery} item={item} />}
                                                                                    {item.data_type === "Meeting" && <Meeting query={debouncedQuery} meetingData={item} />}
                                                                                    {item.data_type === 'Reminder' && <Reminder query={debouncedQuery} reminder={item} />}
                                                                                    {item.data_type === "Activity" && <Activity query={debouncedQuery} item={item} />}
                                                                                    {item.data_type === 'Email' && <Email query={debouncedQuery} email={item} />}
                                                                                    {item.data_type === 'Vault' && <Vault query={debouncedQuery} document={item} />}
                                                                                </div>
                                                                            )}
                                                                        </Combobox.Option>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </Combobox.Options>
                                        </div>
                                    )}

                                    {!isLoading && query !== '' && numberOfResults === 0 && (
                                        <div className="px-6 py-14 text-center sm:px-14">
                                            <XCircleIcon className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                                            <p className="mt-4 text-sm text-gray-900">
                                                We couldn't find any items with that term. Please try again.
                                            </p>
                                        </div>
                                    )}
                                </Combobox>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root></>
    )
}
